export const PORTAL_RENDER_FAILURE_REPORT_VERSION = 1 as const;
export const PORTAL_ROUTE_BOUNDARY_ID = "portal-route" as const;
export const PORTAL_ROOT_BOUNDARY_ID = "portal-root" as const;
export const PORTAL_REACT_ROOT_BOUNDARY_ID = "react-root" as const;

export type PortalRenderFailureCode =
  | "PORTAL_ROUTE_RENDER_FAILED"
  | "PORTAL_ROOT_RENDER_FAILED"
  | "REACT_UNCAUGHT_ERROR"
  | "REACT_RECOVERABLE_ERROR";

export type PortalBoundaryId =
  | typeof PORTAL_ROUTE_BOUNDARY_ID
  | typeof PORTAL_ROOT_BOUNDARY_ID
  | typeof PORTAL_REACT_ROOT_BOUNDARY_ID;

export type PortalRouteId =
  | "portal-home"
  | "authentication"
  | "guest"
  | "staff"
  | "admin"
  | "unknown";

export interface PortalBoundaryState {
  readonly failed: boolean;
  readonly navigationEpoch: number;
  readonly retryCount: number;
  readonly remountSequence: number;
}

export interface PortalRenderFailureReport {
  readonly version: typeof PORTAL_RENDER_FAILURE_REPORT_VERSION;
  readonly code: PortalRenderFailureCode;
  readonly boundaryId: PortalBoundaryId;
  readonly routeId: PortalRouteId;
  readonly retryCount: number;
  readonly occurrenceId: string;
}

export type PortalRenderFailureReporter = (
  report: PortalRenderFailureReport,
) => void;

export interface PortalRenderFailureInput {
  readonly code: PortalRenderFailureCode;
  readonly boundaryId: PortalBoundaryId;
  readonly routeId: PortalRouteId;
  readonly retryCount: number;
}

const MAX_RETRY_COUNT = 99;
const MAX_NAVIGATION_EPOCH = Number.MAX_SAFE_INTEGER;
const SAFE_OCCURRENCE_ID = /^[a-z0-9][a-z0-9-]{0,95}$/i;
let fallbackOccurrenceSequence = 0;

function boundedInteger(value: number, maximum: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(Math.trunc(value), 0), maximum);
}

function normalizeOccurrenceId(value: unknown) {
  return typeof value === "string" && SAFE_OCCURRENCE_ID.test(value)
    ? value
    : "portal-occurrence-unavailable";
}

function defaultOccurrenceId() {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return `portal-${uuid}`;
  } catch {
    // A correlation token is optional; recovery must never depend on entropy.
  }

  fallbackOccurrenceSequence =
    (fallbackOccurrenceSequence + 1) % MAX_NAVIGATION_EPOCH;
  return `portal-local-${Date.now().toString(36)}-${fallbackOccurrenceSequence.toString(36)}`;
}

export function createPortalBoundaryState(
  navigationEpoch = 0,
): PortalBoundaryState {
  return {
    failed: false,
    navigationEpoch: boundedInteger(navigationEpoch, MAX_NAVIGATION_EPOCH),
    retryCount: 0,
    remountSequence: 0,
  };
}

export function derivePortalNavigationReset(
  state: PortalBoundaryState,
  navigationEpoch: number,
) {
  const nextEpoch = boundedInteger(navigationEpoch, MAX_NAVIGATION_EPOCH);
  return state.navigationEpoch === nextEpoch
    ? null
    : createPortalBoundaryState(nextEpoch);
}

export function markPortalBoundaryFailed(
  state: PortalBoundaryState,
): PortalBoundaryState {
  return state.failed ? state : { ...state, failed: true };
}

export function retryPortalBoundary(
  state: PortalBoundaryState,
): PortalBoundaryState {
  return {
    ...state,
    failed: false,
    retryCount: boundedInteger(state.retryCount + 1, MAX_RETRY_COUNT),
    remountSequence: boundedInteger(
      state.remountSequence + 1,
      MAX_NAVIGATION_EPOCH,
    ),
  };
}

export function resolvePortalRouteId(pathname: string): PortalRouteId {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  const firstSegment = path.split("/").filter(Boolean)[0];

  if (!firstSegment) return "portal-home";
  if (firstSegment === "auth") return "authentication";
  if (firstSegment === "guest") return "guest";
  if (firstSegment === "staff") return "staff";
  if (firstSegment === "admin") return "admin";
  return "unknown";
}

export function createPortalRenderFailureReport(
  input: PortalRenderFailureInput,
  occurrenceIdFactory: () => unknown = defaultOccurrenceId,
): PortalRenderFailureReport {
  let occurrenceId: unknown;

  try {
    occurrenceId = occurrenceIdFactory();
  } catch {
    occurrenceId = undefined;
  }

  return Object.freeze({
    version: PORTAL_RENDER_FAILURE_REPORT_VERSION,
    code: input.code,
    boundaryId: input.boundaryId,
    routeId: input.routeId,
    retryCount: boundedInteger(input.retryCount, MAX_RETRY_COUNT),
    occurrenceId: normalizeOccurrenceId(occurrenceId),
  });
}

export function reportPortalRenderFailure(
  reporter: PortalRenderFailureReporter | null | undefined,
  report: PortalRenderFailureReport,
) {
  if (!reporter) return false;

  try {
    reporter(report);
    return true;
  } catch {
    return false;
  }
}
